import { useEffect, useState } from 'react'
import type { ServiceId } from './serviceData'

export type JobStatus =
  | 'bidding_open'
  | 'bidding_closed'
  | 'pending_payment'
  | 'active'
  | 'completed'
  | 'cancelled'

export type JobType = 'booking' | 'bidding'

export type BidStatus =
  | 'pending'
  | 'accepted'
  | 'rejected'
  | 'expired'
  | 'cancelled'

export interface JobBid {
  supplier: string
  price: number
  rating: number
  initials: string
  color: string
  receivedAt: number
  status?: BidStatus // omitted = pending
  truck?: string
}

export interface JobRecord {
  id: string
  type: JobType
  status: JobStatus
  serviceId: ServiceId
  serviceName: string
  prompt: string
  routeHeadline: string
  pickup?: string
  dropoff?: string
  supplierName?: string
  supplierTruck?: string
  price?: number
  estimatedPrice?: number
  createdAt: number
  closesAt?: number // for bidding
  startedAt?: number
  completedAt?: number
  cancelledAt?: number
  bids?: JobBid[]
  cancellationReason?: string
  photos?: string[]
  rating?: number // 1-5
  reviewText?: string
  ratingTags?: string[]
  ratedAt?: number
}

const STORAGE_KEY = 'pair2.activity'
const SEARCH_KEY = 'pair2.searches'
const CHANNEL = 'pair2.activity.changed'

export interface SearchRecord {
  id: string
  prompt: string
  serviceId: ServiceId
  serviceName: string
  createdAt: number
}

let searchCache: SearchRecord[] | null = null

function loadSearches(): SearchRecord[] {
  if (searchCache) return searchCache
  try {
    const raw = sessionStorage.getItem(SEARCH_KEY)
    searchCache = raw ? (JSON.parse(raw) as SearchRecord[]) : []
  } catch {
    searchCache = []
  }
  return searchCache!
}

function saveSearches(next: SearchRecord[]) {
  searchCache = next
  try {
    sessionStorage.setItem(SEARCH_KEY, JSON.stringify(next))
  } catch {
    // ignore
  }
  listeners.forEach((l) => l())
  try {
    window.dispatchEvent(new Event(CHANNEL))
  } catch {
    // ignore
  }
}

// ----- In-memory cache + listeners -----------------------------------------
let cache: JobRecord[] | null = null
const listeners = new Set<() => void>()

function load(): JobRecord[] {
  if (cache) return cache
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    cache = raw ? (JSON.parse(raw) as JobRecord[]) : []
  } catch {
    cache = []
  }
  return cache!
}

function save(next: JobRecord[]) {
  cache = next
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next))
  } catch {
    // ignore
  }
  listeners.forEach((l) => l())
  try {
    window.dispatchEvent(new Event(CHANNEL))
  } catch {
    // ignore
  }
}

function genId(prefix: string) {
  return (
    prefix +
    '_' +
    Math.random().toString(36).slice(2, 8) +
    Math.random().toString(36).slice(2, 4)
  )
}

// ----- Public API ----------------------------------------------------------
export function listJobs(): JobRecord[] {
  return [...load()].sort((a, b) => b.createdAt - a.createdAt)
}

export function getJob(id: string): JobRecord | null {
  return load().find((j) => j.id === id) || null
}

export function findOpenBidding(serviceId: ServiceId): JobRecord | null {
  const list = load()
  return (
    list.find(
      (j) =>
        j.type === 'bidding' &&
        j.status === 'bidding_open' &&
        j.serviceId === serviceId,
    ) || null
  )
}

export function createBidding(input: {
  serviceId: ServiceId
  serviceName: string
  prompt: string
  routeHeadline: string
  pickup?: string
  dropoff?: string
  estimatedPrice?: number
  photos?: string[]
}): JobRecord {
  const now = Date.now()
  const job: JobRecord = {
    id: genId('bid'),
    type: 'bidding',
    status: 'bidding_open',
    serviceId: input.serviceId,
    serviceName: input.serviceName,
    prompt: input.prompt,
    routeHeadline: input.routeHeadline,
    pickup: input.pickup,
    dropoff: input.dropoff,
    estimatedPrice: input.estimatedPrice,
    createdAt: now,
    closesAt: now + 24 * 60 * 60 * 1000,
    bids: [],
    photos: input.photos && input.photos.length ? input.photos : undefined,
  }
  save([...load(), job])
  return job
}

export function addBid(jobId: string, bid: JobBid) {
  const list = load()
  const idx = list.findIndex((j) => j.id === jobId)
  if (idx < 0) return
  const job = list[idx]
  if (job.type !== 'bidding') return
  const nextBids = [...(job.bids || []), bid]
  list[idx] = { ...job, bids: nextBids }
  save(list)
}

export function createBooking(input: {
  serviceId: ServiceId
  serviceName: string
  prompt: string
  routeHeadline: string
  pickup?: string
  dropoff?: string
  supplierName: string
  supplierTruck: string
  price: number
  photos?: string[]
}): JobRecord {
  const job: JobRecord = {
    id: genId('job'),
    type: 'booking',
    status: 'pending_payment',
    serviceId: input.serviceId,
    serviceName: input.serviceName,
    prompt: input.prompt,
    routeHeadline: input.routeHeadline,
    pickup: input.pickup,
    dropoff: input.dropoff,
    supplierName: input.supplierName,
    supplierTruck: input.supplierTruck,
    price: input.price,
    createdAt: Date.now(),
    photos: input.photos && input.photos.length ? input.photos : undefined,
  }
  save([...load(), job])
  return job
}

export function payAndStart(jobId: string) {
  const list = load()
  const idx = list.findIndex((j) => j.id === jobId)
  if (idx < 0) return
  list[idx] = { ...list[idx], status: 'active', startedAt: Date.now() }
  save(list)
}

export function completeJob(jobId: string) {
  const list = load()
  const idx = list.findIndex((j) => j.id === jobId)
  if (idx < 0) return
  list[idx] = { ...list[idx], status: 'completed', completedAt: Date.now() }
  save(list)
}

export function cancelJob(jobId: string, reason?: string) {
  const list = load()
  const idx = list.findIndex((j) => j.id === jobId)
  if (idx < 0) return
  const job = list[idx]
  // For bidding jobs, mark any pending bids as cancelled so the history reflects user action.
  const nextBids = job.bids
    ? job.bids.map((b) =>
        !b.status || b.status === 'pending'
          ? { ...b, status: 'cancelled' as BidStatus }
          : b,
      )
    : undefined
  list[idx] = {
    ...job,
    status: 'cancelled',
    cancelledAt: Date.now(),
    cancellationReason: reason,
    bids: nextBids,
  }
  save(list)
}

// Mark a single bid by index as the chosen winner; reject the others.
// Transitions the bidding job to pending_payment with the accepted bid's
// details so the Payment page can finalize and start the job.
export function acceptBid(jobId: string, bidIndex: number): JobRecord | null {
  const list = load()
  const idx = list.findIndex((j) => j.id === jobId)
  if (idx < 0) return null
  const job = list[idx]
  if (!job.bids || !job.bids[bidIndex]) return null

  const accepted = job.bids[bidIndex]
  const nextBids: JobBid[] = job.bids.map((b, i) => {
    if (i === bidIndex) return { ...b, status: 'accepted' }
    if (!b.status || b.status === 'pending') return { ...b, status: 'rejected' }
    return b
  })

  const updated: JobRecord = {
    ...job,
    bids: nextBids,
    status: 'pending_payment',
    supplierName: accepted.supplier,
    supplierTruck: accepted.truck || 'Carrier-selected truck',
    price: accepted.price,
  }
  list[idx] = updated
  save(list)
  return updated
}

// Reject a single bid without closing the request.
export function rejectBid(jobId: string, bidIndex: number) {
  const list = load()
  const idx = list.findIndex((j) => j.id === jobId)
  if (idx < 0) return
  const job = list[idx]
  if (!job.bids || !job.bids[bidIndex]) return
  const nextBids = job.bids.map((b, i) =>
    i === bidIndex && (!b.status || b.status === 'pending')
      ? { ...b, status: 'rejected' as BidStatus }
      : b,
  )
  list[idx] = { ...job, bids: nextBids }
  save(list)
}

// View-time helper: any bid still 'pending' after closesAt is considered expired.
// ----- Rating ---------------------------------------------------------------
export function rateJob(
  jobId: string,
  rating: number,
  reviewText?: string,
  tags?: string[],
) {
  const list = load()
  const idx = list.findIndex((j) => j.id === jobId)
  if (idx < 0) return
  list[idx] = {
    ...list[idx],
    rating,
    reviewText: reviewText?.trim() || undefined,
    ratingTags: tags && tags.length ? tags : undefined,
    ratedAt: Date.now(),
  }
  save(list)
}

export function bidsWithLifecycle(job: JobRecord): JobBid[] {
  if (!job.bids) return []
  const closed =
    job.status === 'bidding_closed' ||
    job.status === 'cancelled' ||
    (job.closesAt && Date.now() > job.closesAt)
  return job.bids.map((b) =>
    closed && (!b.status || b.status === 'pending')
      ? { ...b, status: 'expired' as BidStatus }
      : b,
  )
}

export function closeBidding(jobId: string) {
  const list = load()
  const idx = list.findIndex((j) => j.id === jobId)
  if (idx < 0) return
  list[idx] = { ...list[idx], status: 'bidding_closed' }
  save(list)
}

// ----- Search history ------------------------------------------------------
export function listSearches(): SearchRecord[] {
  return [...loadSearches()].sort((a, b) => b.createdAt - a.createdAt)
}

export function recordSearch(input: {
  prompt: string
  serviceId: ServiceId
  serviceName: string
}) {
  if (!input.prompt || !input.prompt.trim()) return
  const list = loadSearches()
  const fiveMinAgo = Date.now() - 5 * 60 * 1000
  if (
    list.some(
      (s) =>
        s.prompt.trim().toLowerCase() === input.prompt.trim().toLowerCase() &&
        s.createdAt > fiveMinAgo,
    )
  )
    return
  const record: SearchRecord = {
    id: 'sch_' + Math.random().toString(36).slice(2, 8),
    prompt: input.prompt.trim(),
    serviceId: input.serviceId,
    serviceName: input.serviceName,
    createdAt: Date.now(),
  }
  saveSearches([record, ...list].slice(0, 30))
}

export function clearSearches() {
  saveSearches([])
}

// ----- Counter-offer negotiation ------------------------------------------
const OFFERS_KEY = 'pair2.offers'

export type OfferStatus =
  | 'pending' // user has requested, waiting on supplier
  | 'countered' // supplier (or user) has responded with a different price; needs other side to act
  | 'accepted'
  | 'rejected'
  | 'expired'

export interface OfferTurn {
  by: 'user' | 'supplier'
  price: number
  message?: string
  at: number
}

export interface OfferRecord {
  id: string
  serviceId: ServiceId
  serviceName: string
  prompt: string
  routeHeadline: string
  supplierName: string
  supplierImage?: string
  supplierTruck?: string
  originalPrice: number
  currentPrice: number
  currentSide: 'user' | 'supplier' // who proposed currentPrice
  status: OfferStatus
  createdAt: number
  updatedAt: number
  history: OfferTurn[]
  photos?: string[]
  unread?: boolean // true when supplier has just responded and user hasn't viewed
}

let offersCache: OfferRecord[] | null = null

function loadOffers(): OfferRecord[] {
  if (offersCache) return offersCache
  try {
    const raw = sessionStorage.getItem(OFFERS_KEY)
    offersCache = raw ? (JSON.parse(raw) as OfferRecord[]) : []
  } catch {
    offersCache = []
  }
  return offersCache!
}

function saveOffers(next: OfferRecord[]) {
  offersCache = next
  try {
    sessionStorage.setItem(OFFERS_KEY, JSON.stringify(next))
  } catch {
    // ignore
  }
  listeners.forEach((l) => l())
  try {
    window.dispatchEvent(new Event(CHANNEL))
  } catch {
    // ignore
  }
}

export function listOffers(): OfferRecord[] {
  return [...loadOffers()].sort((a, b) => b.updatedAt - a.updatedAt)
}

export function getOffer(id: string): OfferRecord | null {
  return loadOffers().find((o) => o.id === id) || null
}

export function createOffer(input: {
  serviceId: ServiceId
  serviceName: string
  prompt: string
  routeHeadline: string
  supplierName: string
  supplierImage?: string
  supplierTruck?: string
  originalPrice: number
  requestedPrice: number
  message?: string
  photos?: string[]
}): OfferRecord {
  const now = Date.now()
  const offer: OfferRecord = {
    id: 'off_' + Math.random().toString(36).slice(2, 8),
    serviceId: input.serviceId,
    serviceName: input.serviceName,
    prompt: input.prompt,
    routeHeadline: input.routeHeadline,
    supplierName: input.supplierName,
    supplierImage: input.supplierImage,
    supplierTruck: input.supplierTruck,
    originalPrice: input.originalPrice,
    currentPrice: input.requestedPrice,
    currentSide: 'user',
    status: 'pending',
    createdAt: now,
    updatedAt: now,
    history: [
      {
        by: 'user',
        price: input.requestedPrice,
        message: input.message,
        at: now,
      },
    ],
    photos: input.photos && input.photos.length ? input.photos : undefined,
    unread: false,
  }
  saveOffers([...loadOffers(), offer])
  // Schedule simulated supplier response after a short delay (demo).
  scheduleSupplierResponse(offer.id)
  return offer
}

function scheduleSupplierResponse(offerId: string) {
  const delay = 6000 + Math.random() * 6000 // 6–12s
  setTimeout(() => {
    const list = loadOffers()
    const idx = list.findIndex((o) => o.id === offerId)
    if (idx < 0) return
    const offer = list[idx]
    if (offer.status !== 'pending') return
    // Supplier counter-logic: split the gap, slightly favoring the original price.
    // ~60% toward original, 40% toward user's ask. With minor jitter.
    const gap = offer.originalPrice - offer.currentPrice
    const counter = Math.round(
      offer.currentPrice + gap * (0.55 + Math.random() * 0.15),
    )
    // 15% chance the supplier just accepts the user's price outright (when ask is reasonable).
    const acceptOutright =
      Math.random() < 0.15 && offer.currentPrice >= offer.originalPrice * 0.88
    if (acceptOutright) {
      const now = Date.now()
      list[idx] = {
        ...offer,
        status: 'countered',
        currentPrice: offer.currentPrice,
        currentSide: 'supplier',
        updatedAt: now,
        unread: true,
        history: [
          ...offer.history,
          {
            by: 'supplier',
            price: offer.currentPrice,
            message: 'Deal — we can match your price. Book within 30 mins.',
            at: now,
          },
        ],
      }
    } else {
      const now = Date.now()
      const messages = [
        'Best I can do given fuel and crew rates.',
        'I can do this — locks in if you book in the next 30 mins.',
        'Halfway there. Let me know if this works.',
        "I'll throw in 20 mins of waiting time at this price.",
      ]
      list[idx] = {
        ...offer,
        status: 'countered',
        currentPrice: counter,
        currentSide: 'supplier',
        updatedAt: now,
        unread: true,
        history: [
          ...offer.history,
          {
            by: 'supplier',
            price: counter,
            message: messages[Math.floor(Math.random() * messages.length)],
            at: now,
          },
        ],
      }
    }
    saveOffers(list)
  }, delay)
}

export function submitUserCounter(
  offerId: string,
  price: number,
  message?: string,
): OfferRecord | null {
  const list = loadOffers()
  const idx = list.findIndex((o) => o.id === offerId)
  if (idx < 0) return null
  const offer = list[idx]
  if (
    offer.status === 'accepted' ||
    offer.status === 'rejected' ||
    offer.status === 'expired'
  )
    return null
  const now = Date.now()
  const updated: OfferRecord = {
    ...offer,
    status: 'pending',
    currentPrice: price,
    currentSide: 'user',
    updatedAt: now,
    unread: false,
    history: [...offer.history, { by: 'user', price, message, at: now }],
  }
  list[idx] = updated
  saveOffers(list)
  scheduleSupplierResponse(offerId)
  return updated
}

export function markOfferRead(offerId: string) {
  const list = loadOffers()
  const idx = list.findIndex((o) => o.id === offerId)
  if (idx < 0) return
  if (!list[idx].unread) return
  list[idx] = { ...list[idx], unread: false }
  saveOffers(list)
}

export function acceptOffer(offerId: string): OfferRecord | null {
  const list = loadOffers()
  const idx = list.findIndex((o) => o.id === offerId)
  if (idx < 0) return null
  const offer = list[idx]
  list[idx] = {
    ...offer,
    status: 'accepted',
    updatedAt: Date.now(),
    unread: false,
  }
  saveOffers(list)
  return list[idx]
}

export function rejectOffer(offerId: string) {
  const list = loadOffers()
  const idx = list.findIndex((o) => o.id === offerId)
  if (idx < 0) return
  list[idx] = {
    ...list[idx],
    status: 'rejected',
    updatedAt: Date.now(),
    unread: false,
  }
  saveOffers(list)
}

export function unreadOfferCount(): number {
  return loadOffers().filter((o) => o.unread).length
}

// ----- Demo data seeding ---------------------------------------------------
// Populates the store with realistic activity covering every status across
// every tab. Runs once per session so it doesn't trample user-created data.
const SEED_FLAG = 'pair2.seeded.v2'

export function seedDemoData() {
  if (typeof window === 'undefined') return
  try {
    if (sessionStorage.getItem(SEED_FLAG) === 'true') return
  } catch {
    return
  }
  // Only seed if both jobs and offers are empty (don't disturb returning users).
  if (loadJobs().length > 0 || loadOffers().length > 0) {
    try {
      sessionStorage.setItem(SEED_FLAG, 'true')
    } catch {}
    return
  }

  const now = Date.now()
  const min = 60 * 1000
  const hr = 60 * min
  const day = 24 * hr

  const seedPhotos = [
    'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?auto=format&fit=crop&q=80&w=400',
    'https://images.unsplash.com/photo-1493663284031-b7e3aefcae8e?auto=format&fit=crop&q=80&w=400',
    'https://images.unsplash.com/photo-1567016432779-094069958ea5?auto=format&fit=crop&q=80&w=400',
  ]
  const furniturePhotos = [
    'https://images.unsplash.com/photo-1555041469-a586c61ea9bc?auto=format&fit=crop&q=80&w=400',
    'https://images.unsplash.com/photo-1540574163026-643ea20ade25?auto=format&fit=crop&q=80&w=400',
  ]
  const carPhotos = [
    'https://images.unsplash.com/photo-1494976388531-d1058494cdd8?auto=format&fit=crop&q=80&w=400',
  ]

  const jobs: JobRecord[] = [
    // 1. ACTIVE booking — currently in transit
    {
      id: genId('job'),
      type: 'booking',
      status: 'active',
      serviceId: 'home',
      serviceName: 'Home Moving',
      prompt:
        'Moving a 2-bedroom apartment from Marina to Downtown next Saturday',
      routeHeadline: 'Dubai Marina → Downtown Dubai',
      pickup: 'Dubai Marina',
      dropoff: 'Downtown Dubai',
      supplierName: 'FastTrack Logistics',
      supplierTruck: '3-Ton Box Truck',
      price: 850,
      createdAt: now - 4 * hr,
      startedAt: now - 30 * min,
      photos: seedPhotos,
    },
    // 2. PENDING PAYMENT — supplier accepted, awaiting checkout
    {
      id: genId('job'),
      type: 'booking',
      status: 'pending_payment',
      serviceId: 'furniture',
      serviceName: 'Furniture Delivery',
      prompt: 'Pick up a sofa and dining set from IKEA Festival City',
      routeHeadline: 'Festival City → Jumeirah Village Circle',
      pickup: 'Festival City',
      dropoff: 'Jumeirah Village Circle',
      supplierName: 'BlueLine Express',
      supplierTruck: '1.5-Ton Pickup',
      price: 320,
      createdAt: now - 25 * min,
      photos: furniturePhotos,
    },
    // 3. COMPLETED booking — last week
    {
      id: genId('job'),
      type: 'booking',
      status: 'completed',
      serviceId: 'recovery',
      serviceName: 'Vehicle Recovery',
      prompt: 'Recover my car from a parking accident in Business Bay',
      routeHeadline: 'Business Bay → Al Quoz garage',
      pickup: 'Business Bay',
      dropoff: 'Al Quoz garage',
      supplierName: 'Sahara Recovery',
      supplierTruck: 'Flatbed recovery truck',
      price: 280,
      createdAt: now - 6 * day,
      startedAt: now - 6 * day + 20 * min,
      completedAt: now - 6 * day + 80 * min,
      photos: carPhotos,
    },
    // 4. COMPLETED — goods delivery
    {
      id: genId('job'),
      type: 'booking',
      status: 'completed',
      serviceId: 'goods',
      serviceName: 'Pallet Delivery',
      prompt: '4 pallets from Jebel Ali warehouse to Sharjah industrial area',
      routeHeadline: 'Jebel Ali → Sharjah Industrial',
      pickup: 'Jebel Ali',
      dropoff: 'Sharjah Industrial',
      supplierName: 'GulfCargo Solutions',
      supplierTruck: '5-Ton Curtain-side',
      price: 540,
      createdAt: now - 3 * day,
      startedAt: now - 3 * day + 45 * min,
      completedAt: now - 3 * day + 2 * hr,
      rating: 5,
      reviewText:
        'Smooth pickup and on-time delivery. Driver kept us updated the whole way.',
      ratingTags: ['On time', 'Good communication', 'Would book again'],
      ratedAt: now - 3 * day + 3 * hr,
    },
    // 5. CANCELLED — heavy haul
    {
      id: genId('job'),
      type: 'booking',
      status: 'cancelled',
      serviceId: 'heavy',
      serviceName: 'Heavy Equipment Hauling',
      prompt: 'Move an excavator from Al Quoz to Hatta site',
      routeHeadline: 'Al Quoz → Hatta',
      pickup: 'Al Quoz',
      dropoff: 'Hatta',
      supplierName: 'HeavyMove UAE',
      supplierTruck: 'Low-bed 40-Ton',
      price: 2400,
      createdAt: now - 2 * day,
      cancelledAt: now - 2 * day + 4 * hr,
      cancellationReason: 'Project postponed by client',
    },
    // 6. BIDDING OPEN — construction, 3 bids pending
    {
      id: genId('bid'),
      type: 'bidding',
      status: 'bidding_open',
      serviceId: 'construction',
      serviceName: 'Construction Materials',
      prompt:
        'Need to move 12m steel beams from DIP to a tower site in Business Bay tomorrow morning',
      routeHeadline: 'Dubai Investments Park → Business Bay',
      pickup: 'Dubai Investments Park',
      dropoff: 'Business Bay',
      estimatedPrice: 1450,
      createdAt: now - 3 * hr,
      closesAt: now + 21 * hr,
      bids: [
        {
          supplier: 'Al Khaleej Movers',
          price: 1280,
          rating: 4.6,
          initials: 'AK',
          color: 'bg-amber-100 text-amber-700',
          receivedAt: now - 2 * hr,
        },
        {
          supplier: 'BlueLine Express',
          price: 1190,
          rating: 4.8,
          initials: 'BL',
          color: 'bg-blue-100 text-blue-700',
          receivedAt: now - 90 * min,
        },
        {
          supplier: 'Sahara Logistics',
          price: 1240,
          rating: 4.7,
          initials: 'SL',
          color: 'bg-rose-100 text-rose-700',
          receivedAt: now - 45 * min,
        },
      ],
    },
    // 7. BIDDING OPEN — chiller, 4 bids streaming in
    {
      id: genId('bid'),
      type: 'bidding',
      status: 'bidding_open',
      serviceId: 'chiller',
      serviceName: 'Chiller Transport',
      prompt:
        'Frozen seafood, 18 pallets from Al Aweer to Abu Dhabi cold-storage',
      routeHeadline: 'Al Aweer → Abu Dhabi',
      pickup: 'Al Aweer',
      dropoff: 'Abu Dhabi',
      estimatedPrice: 1800,
      createdAt: now - 8 * hr,
      closesAt: now + 16 * hr,
      bids: [
        {
          supplier: 'Cold Chain UAE',
          price: 1620,
          rating: 4.9,
          initials: 'CC',
          color: 'bg-cyan-100 text-cyan-700',
          receivedAt: now - 7 * hr,
        },
        {
          supplier: 'PolarFreight',
          price: 1580,
          rating: 4.7,
          initials: 'PF',
          color: 'bg-indigo-100 text-indigo-700',
          receivedAt: now - 5 * hr,
        },
        {
          supplier: 'GulfCargo Solutions',
          price: 1650,
          rating: 4.5,
          initials: 'GC',
          color: 'bg-purple-100 text-purple-700',
          receivedAt: now - 2 * hr,
        },
        {
          supplier: 'Sahara Logistics',
          price: 1540,
          rating: 4.7,
          initials: 'SL',
          color: 'bg-rose-100 text-rose-700',
          receivedAt: now - 30 * min,
        },
      ],
    },
    // 8. BIDDING that was ACCEPTED → completed
    {
      id: genId('bid'),
      type: 'bidding',
      status: 'completed',
      serviceId: 'home',
      serviceName: 'Home Moving',
      prompt: 'Studio relocation from JLT to Silicon Oasis',
      routeHeadline: 'JLT → Dubai Silicon Oasis',
      pickup: 'JLT',
      dropoff: 'Dubai Silicon Oasis',
      supplierName: 'Sahara Logistics',
      supplierTruck: '2-Ton Box Truck',
      price: 620,
      estimatedPrice: 720,
      createdAt: now - 9 * day,
      startedAt: now - 8 * day,
      completedAt: now - 8 * day + 3 * hr,
      bids: [
        {
          supplier: 'FastTrack Logistics',
          price: 690,
          rating: 4.8,
          initials: 'FT',
          color: 'bg-blue-100 text-blue-700',
          receivedAt: now - 9 * day + 1 * hr,
          status: 'rejected',
        },
        {
          supplier: 'Sahara Logistics',
          price: 620,
          rating: 4.7,
          initials: 'SL',
          color: 'bg-rose-100 text-rose-700',
          receivedAt: now - 9 * day + 2 * hr,
          status: 'accepted',
        },
        {
          supplier: 'Al Khaleej Movers',
          price: 710,
          rating: 4.6,
          initials: 'AK',
          color: 'bg-amber-100 text-amber-700',
          receivedAt: now - 9 * day + 3 * hr,
          status: 'rejected',
        },
      ],
    },
    // 9. BIDDING that was CANCELLED
    {
      id: genId('bid'),
      type: 'bidding',
      status: 'cancelled',
      serviceId: 'furniture',
      serviceName: 'Furniture Delivery',
      prompt: 'Wardrobe and mattress from Mall of the Emirates to Mirdif',
      routeHeadline: 'Mall of the Emirates → Mirdif',
      pickup: 'Mall of the Emirates',
      dropoff: 'Mirdif',
      estimatedPrice: 240,
      createdAt: now - 12 * day,
      cancelledAt: now - 11 * day,
      cancellationReason: 'Found a friend with a truck',
      bids: [
        {
          supplier: 'BlueLine Express',
          price: 210,
          rating: 4.8,
          initials: 'BL',
          color: 'bg-blue-100 text-blue-700',
          receivedAt: now - 12 * day + 1 * hr,
          status: 'cancelled',
        },
        {
          supplier: 'Al Khaleej Movers',
          price: 235,
          rating: 4.6,
          initials: 'AK',
          color: 'bg-amber-100 text-amber-700',
          receivedAt: now - 12 * day + 2 * hr,
          status: 'cancelled',
        },
      ],
    },
  ]
  saveJobs(jobs)

  // ----- Counter offers -----
  const offers: OfferRecord[] = [
    // 1. PENDING — user just sent, waiting on supplier
    {
      id: 'off_demo01',
      serviceId: 'goods',
      serviceName: 'Pallet Delivery',
      prompt: '6 pallets from DIP to Sharjah',
      routeHeadline: 'DIP → Sharjah',
      supplierName: 'GulfCargo Solutions',
      supplierTruck: '5-Ton Curtain-side',
      originalPrice: 620,
      currentPrice: 540,
      currentSide: 'user',
      status: 'pending',
      createdAt: now - 4 * min,
      updatedAt: now - 4 * min,
      history: [
        {
          by: 'user',
          price: 540,
          message: 'Repeat customer — can you match this?',
          at: now - 4 * min,
        },
      ],
      unread: false,
    },
    // 2. COUNTERED — carrier has responded, unread
    {
      id: 'off_demo02',
      serviceId: 'furniture',
      serviceName: 'Furniture Delivery',
      prompt: 'Sofa + dining table, Festival City → JVC',
      routeHeadline: 'Festival City → JVC',
      supplierName: 'BlueLine Express',
      supplierTruck: '1.5-Ton Pickup',
      originalPrice: 460,
      currentPrice: 420,
      currentSide: 'supplier',
      status: 'countered',
      createdAt: now - 35 * min,
      updatedAt: now - 8 * min,
      photos: furniturePhotos,
      history: [
        {
          by: 'user',
          price: 380,
          message: 'Flexible on timing',
          at: now - 35 * min,
        },
        {
          by: 'supplier',
          price: 420,
          message:
            'Best I can do given the second-floor stairs. Locks in if you book within 30 mins.',
          at: now - 8 * min,
        },
      ],
      unread: true,
    },
    // 3. COUNTERED — multi-round, supplier just responded, unread
    {
      id: 'off_demo03',
      serviceId: 'construction',
      serviceName: 'Construction Materials',
      prompt: 'Steel beams DIP → Business Bay',
      routeHeadline: 'DIP → Business Bay',
      supplierName: 'HeavyMove UAE',
      supplierTruck: 'Flatbed 12m',
      originalPrice: 1450,
      currentPrice: 1290,
      currentSide: 'supplier',
      status: 'countered',
      createdAt: now - 3 * hr,
      updatedAt: now - 12 * min,
      history: [
        {
          by: 'user',
          price: 1150,
          message: 'Have 3 similar moves coming up this month',
          at: now - 3 * hr,
        },
        {
          by: 'supplier',
          price: 1340,
          message: 'I can do this if it locks in for the recurring jobs.',
          at: now - 2 * hr,
        },
        {
          by: 'user',
          price: 1250,
          message: 'Meet in the middle?',
          at: now - 50 * min,
        },
        {
          by: 'supplier',
          price: 1290,
          message: 'Deal at this price — book within the hour.',
          at: now - 12 * min,
        },
      ],
      unread: true,
    },
    // 4. ACCEPTED offer
    {
      id: 'off_demo04',
      serviceId: 'home',
      serviceName: 'Home Moving',
      prompt: '2BR Marina → Downtown',
      routeHeadline: 'Dubai Marina → Downtown Dubai',
      supplierName: 'FastTrack Logistics',
      supplierTruck: '3-Ton Box Truck',
      originalPrice: 850,
      currentPrice: 720,
      currentSide: 'user',
      status: 'accepted',
      createdAt: now - 5 * day,
      updatedAt: now - 5 * day + 2 * hr,
      history: [
        {
          by: 'user',
          price: 700,
          message: 'Booking same-day, hoping for a discount',
          at: now - 5 * day,
        },
        {
          by: 'supplier',
          price: 750,
          message: 'I can do 750 if flexible on the time window.',
          at: now - 5 * day + 1 * hr,
        },
        {
          by: 'user',
          price: 720,
          at: now - 5 * day + 90 * min,
        },
        {
          by: 'supplier',
          price: 720,
          message: 'Deal. See you tomorrow.',
          at: now - 5 * day + 2 * hr,
        },
      ],
      unread: false,
    },
    // 5. REJECTED offer
    {
      id: 'off_demo05',
      serviceId: 'heavy',
      serviceName: 'Heavy Equipment Hauling',
      prompt: 'Crane move Al Quoz → Abu Dhabi',
      routeHeadline: 'Al Quoz → Abu Dhabi',
      supplierName: 'HeavyMove UAE',
      supplierTruck: 'Low-bed 60-Ton',
      originalPrice: 4200,
      currentPrice: 2800,
      currentSide: 'user',
      status: 'rejected',
      createdAt: now - 8 * day,
      updatedAt: now - 8 * day + 30 * min,
      history: [
        {
          by: 'user',
          price: 2800,
          message: 'Tight budget',
          at: now - 8 * day,
        },
        {
          by: 'supplier',
          price: 3800,
          message: "Can't go below 3800 — escort + permits cost alone.",
          at: now - 8 * day + 20 * min,
        },
        {
          by: 'user',
          price: 2800,
          at: now - 8 * day + 25 * min,
        },
      ],
      unread: false,
    },
  ]
  saveOffers(offers)

  // ----- Search history -----
  const searches: SearchRecord[] = [
    {
      id: 'sch_demo01',
      prompt: 'Move 2-bedroom apartment from Marina to Downtown',
      serviceId: 'home',
      serviceName: 'Home Moving',
      createdAt: now - 1 * hr,
    },
    {
      id: 'sch_demo02',
      prompt: 'Need a flatbed for a forklift from Al Quoz to Sharjah',
      serviceId: 'heavy',
      serviceName: 'Heavy Equipment Hauling',
      createdAt: now - 6 * hr,
    },
    {
      id: 'sch_demo03',
      prompt: 'Pick up sofa and dining set from IKEA',
      serviceId: 'furniture',
      serviceName: 'Furniture Delivery',
      createdAt: now - 2 * day,
    },
    {
      id: 'sch_demo04',
      prompt: 'Frozen seafood 18 pallets to Abu Dhabi',
      serviceId: 'chiller',
      serviceName: 'Chiller Transport',
      createdAt: now - 5 * day,
    },
  ]
  saveSearches(searches)

  try {
    sessionStorage.setItem(SEED_FLAG, 'true')
  } catch {}
}

// Aliases so seed code reads cleanly — they map to existing internal helpers.
const loadJobs = load
const saveJobs = save

// ----- React hook ----------------------------------------------------------
export function useActivity() {
  const [, setTick] = useState(0)
  useEffect(() => {
    seedDemoData()
    const handler = () => setTick((t) => t + 1)
    listeners.add(handler)
    window.addEventListener(CHANNEL, handler)
    window.addEventListener('storage', handler)
    return () => {
      listeners.delete(handler)
      window.removeEventListener(CHANNEL, handler)
      window.removeEventListener('storage', handler)
    }
  }, [])
  return {
    jobs: listJobs(),
    searches: listSearches(),
    offers: listOffers(),
  }
}

// ----- Status display helpers ---------------------------------------------
export function statusLabel(status: JobStatus): string {
  switch (status) {
    case 'bidding_open':
      return 'Bidding open'
    case 'bidding_closed':
      return 'Bidding closed'
    case 'pending_payment':
      return 'Awaiting payment'
    case 'active':
      return 'Active'
    case 'completed':
      return 'Completed'
    case 'cancelled':
      return 'Cancelled'
  }
}

export function statusColor(status: JobStatus): string {
  switch (status) {
    case 'bidding_open':
      return 'bg-amber-50 text-amber-700 border-amber-100'
    case 'bidding_closed':
      return 'bg-gray-100 text-gray-600 border-gray-200'
    case 'pending_payment':
      return 'bg-blue-50 text-blue-700 border-blue-100'
    case 'active':
      return 'bg-green-50 text-green-700 border-green-100'
    case 'completed':
      return 'bg-emerald-50 text-emerald-700 border-emerald-100'
    case 'cancelled':
      return 'bg-rose-50 text-rose-700 border-rose-100'
  }
}

export function isCancellable(status: JobStatus): boolean {
  return (
    status === 'bidding_open' ||
    status === 'pending_payment' ||
    status === 'active'
  )
}
